import { theme, useResponsive } from "@product/scmp-sdk";
import { useThrottledCallback } from "@react-hookz/web";
import type {
  Destructor,
  PlayerState,
  SpeechifyExperienceEvent,
  SpeechifyPlayerConfig,
} from "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useRef } from "react";

import { onlyOnePlayingPlayerAtom } from "~/components/article/article-list/hooks";
import { SpeechSkipClassName } from "~/components/article/article-speech/consts";
import { useArticleSpeechContext } from "~/components/article/article-speech/contexts";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { ZIndexLayer } from "~/lib/styles";

import type { ExtendedEvents } from "./types";

const useBuildSpeechifyEventHandler = () => {
  const { setArticleSpeechifyPlayerState } = useArticleSpeechContext();
  const [{ adsManager, youtubePlayer }, setOnlyOnePlayingPlayerAtom] =
    useAtom(onlyOnePlayingPlayerAtom);

  const hasTriggeredThreeSeconds = useRef(false);

  const sendProgressTrackingEvent = useThrottledCallback(
    ({ label }: { label: string }) => {
      sendGATracking({
        action: "TTS/Progress",
        category: "TTS",
        label,
      });
    },
    [sendGATracking],
    1000,
    true,
  );

  const handleCallingExtendedEventFunctions = (
    event: SpeechifyExperienceEvent["type"],
    extendedEvents?: ExtendedEvents,
  ) => {
    if (!extendedEvents) return;

    (extendedEvents[event] ?? []).forEach(function_ => function_());
  };

  const ensureOnlySpeechifyPlays = useCallback(() => {
    adsManager?.pause();
    void youtubePlayer?.pauseVideo();
    setOnlyOnePlayingPlayerAtom(state => ({ ...state, stopYoutubeAutoplay: true }));
  }, [adsManager, setOnlyOnePlayingPlayerAtom, youtubePlayer]);

  const speechifyEventHandler = useCallback(
    (extendedEvents?: ExtendedEvents) =>
      (event: SpeechifyExperienceEvent, playerState: PlayerState | undefined) => {
        switch (event.type) {
          case "voice-changed":
            sendGATracking({
              action: "TTS/voice changed",
              category: "TTS",
              label: playerState?.voiceDisplayName ?? "",
            });

            break;
          case "speed-changed":
            sendGATracking({
              action: "TTS/speed changed",
              category: "TTS",
              label: `${playerState?.speedWPM ?? ""}`,
            });

            break;
          case "pause":
            setOnlyOnePlayingPlayerAtom(state => ({ ...state, stopYoutubeAutoplay: false }));
            setArticleSpeechifyPlayerState?.("paused");
            sendGATracking({
              action: "TTS/Pause",
              category: "TTS",
              label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
            });

            break;
          case "play":
            ensureOnlySpeechifyPlays();
            setArticleSpeechifyPlayerState?.("playing");
            sendGATracking({
              action: "TTS/Play",
              category: "TTS",
              label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
            });

            break;
          case "progress":
            const percentageElapsed = Math.round((playerState?.progressFraction ?? 0) * 100);
            if (percentageElapsed > 0 && percentageElapsed % 10 === 0) {
              sendProgressTrackingEvent({
                label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
              });
            }

            if (!hasTriggeredThreeSeconds.current && playerState?.audioLengthSeconds) {
              const targetProgressFraction = 3 / playerState.audioLengthSeconds;
              if (playerState?.progressFraction >= targetProgressFraction) {
                sendGATracking({
                  action: `TTS/Speech progress 3s`,
                  category: "TTS",
                });
                hasTriggeredThreeSeconds.current = true;
              }
            }

            break;
          case "progress-finished":
            setOnlyOnePlayingPlayerAtom(state => ({ ...state, stopYoutubeAutoplay: false }));
            sendGATracking({
              action: "TTS/Progress Finished",
              category: "TTS",
              label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
            });

            break;
          case "replay":
            ensureOnlySpeechifyPlays();
            break;
          case "resume":
            ensureOnlySpeechifyPlays();
            setArticleSpeechifyPlayerState?.("playing");
            sendGATracking({
              action: "TTS/Resume",
              category: "TTS",
              label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
            });

            break;
          case "unmount":
            setOnlyOnePlayingPlayerAtom(state => ({ ...state, stopYoutubeAutoplay: false }));
            break;
        }

        handleCallingExtendedEventFunctions(event.type, extendedEvents);
      },
    [
      ensureOnlySpeechifyPlays,
      sendProgressTrackingEvent,
      setArticleSpeechifyPlayerState,
      setOnlyOnePlayingPlayerAtom,
    ],
  );

  return {
    speechifyEventHandler,
  };
};

export const useArticleSpeechPlayerController = () => {
  const {
    articleSpeechifyPlayer,
    currentRootElement,
    setArticleSpeechifyPlayerState,
    setCurrentRootElement,
    setSpeechifyModule,
    speechifyModule,
  } = useArticleSpeechContext();
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const setOnlyOnePlayingPlayer = useSetAtom(onlyOnePlayingPlayerAtom);
  const { speechifyEventHandler } = useBuildSpeechifyEventHandler();

  const handleSpeechifyEventsCleanUp = useRef<OrUndefined<Destructor>>();

  const handleInitSpeechifyModule = () => {
    const run = async () => {
      if (speechifyModule) return;

      const sModule = await import(
        /* webpackIgnore: true */ "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs"
      );

      setSpeechifyModule?.(sModule);
    };

    void run();
  };

  const handleInitNewPlayerAndPlay = async ({
    extendedEvents,
    gaEventLabel = "",
    playerConfig,
  }: {
    extendedEvents?: ExtendedEvents;
    gaEventLabel?: string;
    playerConfig: Partial<SpeechifyPlayerConfig>;
  }) => {
    if (!speechifyModule || !playerConfig.rootElement || articleSpeechifyPlayer.current) return;

    setArticleSpeechifyPlayerState?.("loading");

    setCurrentRootElement?.(playerConfig.rootElement);

    const speechifyWidget = speechifyModule.makeSpeechifyExperience({
      customStyles: {
        playButton: {
          background: "radial-gradient(#ffffff 50%, transparent 50%)",
        },
        primaryColor: "#4585FF",
        widget: {
          borderRadius: "8px",
          bottom: isMobile ? "20px" : "40px",
          zIndex: `${ZIndexLayer.Drawer - 1}`,
        },
      },
      enableAutoScroll: false,
      enableHighlighting: false,
      ignoreElements(element) {
        return (
          element.tagName === "NOSCRIPT" ||
          element.tagName === "STYLE" ||
          element.tagName === "SCRIPT" ||
          element.classList.contains(SpeechSkipClassName)
        );
      },
      ...playerConfig,
      showFeedbackForm: false,
    });

    await speechifyWidget.mount();

    handleSpeechifyEventsCleanUp.current = speechifyWidget.addEventListener(
      speechifyEventHandler(extendedEvents),
    );

    articleSpeechifyPlayer.current = speechifyWidget;
    setOnlyOnePlayingPlayer(previousState => ({
      ...previousState,
      articleSpeechifyPlayer: speechifyWidget,
    }));

    setArticleSpeechifyPlayerState?.("playing");

    sendGATracking({
      action: "TTS/Click",
      category: "TTS",
      label: gaEventLabel,
    });

    speechifyWidget.play();
  };

  const handleCurrentPlayerUnmountAndCleanUp = async () => {
    if (!articleSpeechifyPlayer.current) return;

    setArticleSpeechifyPlayerState?.("unmounted");
    handleSpeechifyEventsCleanUp.current?.();
    await articleSpeechifyPlayer.current?.unmount();
    articleSpeechifyPlayer.current = undefined;

    sendGATracking({
      action: "TTS/Close",
      category: "TTS",
      label: "",
    });
  };

  const handleCheckIsCurrentArticleLinkedToWidget = useCallback(
    (rootElement: HTMLElement | undefined | null) => {
      if (!rootElement) return false;

      return currentRootElement === rootElement;
    },
    [currentRootElement],
  );

  return {
    handleCheckIsCurrentArticleLinkedToWidget,
    handleCurrentPlayerUnmountAndCleanUp,
    handleInitNewPlayerAndPlay,
    handleInitSpeechifyModule,
  };
};
